import { useCallback, useEffect, useRef, useState } from 'react'
import { QueryClient, useQueryClient } from '@tanstack/react-query'

import config from '@config'

import useRiAdTagManagerInit from '@hooks/useRiAdTagManagerInit'
import useVideoSkeleton from '@hooks/useVideoSkeleton'
import useIsRiAdTagManagerScriptLoadError from '@hooks/useIsRiAdTagManagerScriptLoadError'

import { areAdSlotsLoaded, deepCopy, loadSlots, logAd } from '@utils/ads'
import { Sound } from '@components/Ad/types'
import { Vtype } from '@components/Ad/types'
import { registerSlot as admTagManRegisterSlot } from '@components/Ad/utils'

import { IABCategory, UseVideoPlayerPropsInput } from '@widgets/Video/types'
import { getVideoTypeParam } from '@widgets/Video/utils'

const {
  ads: { videoTypeThresholdInSeconds: videoTypeThresholdInSecondsDefault },
} = config

interface RegisterAdProps
  extends Pick<UseVideoPlayerPropsInput, 'syndication' | 'videoTaxonomy'> {
  vtype: Vtype
  videoId: string
  duration: number
  adContainerId: string
  sound: boolean
  autostart: string
  handleAd: ({ vmapUrl, message }: { vmapUrl?: string; message?: any }) => void
  queryClient: QueryClient
  tagManagerInitCode: string
}

const registerAd = ({
  queryClient,
  tagManagerInitCode,
  vtype,
  videoId,
  duration,
  adContainerId,
  syndication,
  videoTaxonomy,
  sound,
  autostart,
  handleAd,
}: RegisterAdProps) => {
  const VODSpecificProps = {
    source: 'Video Player',
    duration,
    targeting: {
      vtype,
      syndication,
      sherlock: videoTaxonomy?.map(({ id }: IABCategory) => id) ?? [],
      sound: (sound ? 'on' : 'off') as Sound,
      autostart,
      player: 'JWPlayer',
    },
  }

  const registrationOptions = {
    slot: 'VMAP_1',
    container: adContainerId,
    events: {
      adReady: handleAd,
      adEmpty: handleAd,
      adError: handleAd,
    },
    ...VODSpecificProps,
  }

  const deepCopiedRegistrationOptions = deepCopy(registrationOptions)

  // Register the VMAP adslot
  admTagManRegisterSlot(registrationOptions)

  const { slot, container } = deepCopiedRegistrationOptions

  const singleLogAd = (loadingBehaviorDescription?: string) => {
    logAd(
      { queryClient },
      `Slot Registered: %c${slot}\n${' '.repeat(11)}%cinitCode:${' '.repeat(8)}%c${tagManagerInitCode}\n${' '.repeat(11)}%cduration:${' '.repeat(8)}%c${duration}\n${' '.repeat(11)}%cvtype:${' '.repeat(11)}%c${vtype}\n${' '.repeat(11)}%cvideoId:${' '.repeat(9)}%c${videoId}\n${' '.repeat(11)}%ccontainer:${' '.repeat(7)}%c${container}\n${' '.repeat(11)}%cloading state:${' '.repeat(3)}%c${loadingBehaviorDescription}\n${' '.repeat(11)}%cconfig:${' '.repeat(8)}`,
      'font-weight: bold;',
      '',
      'font-weight: bold;',
      '',
      'font-weight: bold;',
      '',
      'font-weight: bold;',
      '',
      'font-weight: bold;',
      '',
      'font-weight: bold;',
      '',
      'font-weight: bold;',
      '',
      deepCopiedRegistrationOptions
    )
  }

  if (areAdSlotsLoaded(queryClient)) {
    loadSlots(queryClient)
    singleLogAd(`loaded manually, as batch loading has already happened`)
  } else {
    singleLogAd(`not loaded yet, as batch loading is pending`)
  }
}

export type UseVideoAds = (
  options: Pick<
    UseVideoPlayerPropsInput,
    'syndication' | 'videoTaxonomy' | 'videoId' | 'duration'
  > & {
    adSound: boolean
    autostart: boolean
    disabledVideoAds: boolean
  }
) => {
  isAdsReady: boolean
  adContainerId: string
  adTagURL: string
}

const useVideoAds: UseVideoAds = ({
  adSound,
  duration,
  syndication,
  videoTaxonomy,
  videoId,
  autostart,
  disabledVideoAds,
}) => {
  const queryClient = useQueryClient()
  const tagManagerInitCode = useRiAdTagManagerInit()
  const isRiAdTagManagerScriptLoadError = useIsRiAdTagManagerScriptLoadError()
  const [isAdsReady, setIsAdsReady] = useState<boolean>(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const vmapUrlRef = useRef<string | null>(null)
  const hasAdRegisteredRef = useRef<boolean>(false)

  const { videoTypeThresholdInSeconds = videoTypeThresholdInSecondsDefault } =
    useVideoSkeleton()

  const vtype = getVideoTypeParam({
    duration,
    shortVideoLengthThresholdInSeconds: videoTypeThresholdInSeconds,
  })

  const adContainerId = videoId ? `jw-video-container-${videoId}` : ''

  const onTimeout = useCallback(() => {
    logAd(
      { queryClient, method: 'warn' },
      `%cSLOT LOADING SKIPPED: Timeout of 3000ms exceeded\n${' '.repeat(7)}%cvideoId:${' '.repeat(14)}%c${videoId}`,
      'font-weight: bold; color: #B22222;',
      '',
      'font-weight: bold;'
    )

    setIsAdsReady(true)
  }, [queryClient, videoId])

  const handleCSAIAd = useCallback(
    ({ vmapUrl, message }: { vmapUrl?: string | null; message?: any }) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      if (vmapUrl) {
        vmapUrlRef.current = vmapUrl
      }

      logAd(
        { queryClient, ...(vmapUrl ? {} : { method: 'warn' }) },
        `%cVIDEO AD CALLBACK: %cVMAP${vmapUrl ? '' : ' NOT'} RETRIEVED\n${' '.repeat(vmapUrl ? 11 : 13)}%cvideoId:${' '.repeat(11)}%c${videoId}${message ? `\n${' '.repeat(vmapUrl ? 11 : 13)}%cmessage:${' '.repeat(11)}%c${message}` : '%c%c'}${vmapUrl ? `\n${' '.repeat(11)}%cvmapUrl:${' '.repeat(11)}%c${vmapUrl}` : '%c%c'}`,
        'font-weight: bold; color: #FFA500;',
        `font-weight: bold; color: ${vmapUrl ? '#2E8B57' : '#B22222'};`,
        '',
        'font-weight: bold;',
        '',
        'font-weight: bold;',
        '',
        'font-weight: bold;'
      )

      setIsAdsReady(true)
    },
    [queryClient, videoId]
  )

  const getAdAutostartValue = useCallback(() => {
    /**
     * the targeting values have to be strings
     */
    return `${+!!autostart}`
  }, [autostart])

  useEffect(() => {
    // the ad will not be registered until we have `adContainerId`
    if (!disabledVideoAds && adContainerId) {
      if (tagManagerInitCode || isRiAdTagManagerScriptLoadError) {
        if (!hasAdRegisteredRef.current) {
          hasAdRegisteredRef.current = true

          if (isRiAdTagManagerScriptLoadError) {
            //! If there is a sript loading error it makes no sense to try to load any ad.
            //! Thus, we immediately proceed with the loading of the video.
            logAd(
              { queryClient, method: 'warn' },
              `%cSLOT LOADING SKIPPED: SDK failed to load\n${' '.repeat(11)}%cvideoId:${' '.repeat(14)}%c${videoId}`,
              'font-weight: bold; color: #B22222;',
              '',
              'font-weight: bold;'
            )

            setIsAdsReady(true)
          } else {
            timeoutRef.current = setTimeout(onTimeout, 3000)

            registerAd({
              queryClient,
              tagManagerInitCode,
              vtype,
              videoId,
              duration,
              adContainerId,
              syndication,
              videoTaxonomy,
              sound: adSound,
              autostart: getAdAutostartValue(),
              handleAd: handleCSAIAd,
            })
          }
        }
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [
    adContainerId,
    adSound,
    disabledVideoAds,
    duration,
    getAdAutostartValue,
    handleCSAIAd,
    isRiAdTagManagerScriptLoadError,
    onTimeout,
    queryClient,
    syndication,
    tagManagerInitCode,
    videoId,
    videoTaxonomy,
    vtype,
  ])

  return {
    adContainerId,
    adTagURL: vmapUrlRef.current ?? '',
    isAdsReady,
  }
}

export default useVideoAds
